import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { v4 as uuidV4 } from "uuid";
import { firestore, storage, FieldValue } from "../../config/firebaseConfig";

// Nonconformance reports, ported from the inventory app's RTDB
// `logs/nonconformance/reports` onto Firestore (`ncReports`). One document
// per report; report numbers keep the legacy `NC_<HEX>` sequence but the
// counter now follows the MPI/UTT contract: a Firestore doc advanced ONLY
// by auto-numbered saves, monotonically, at save time (the old app burned
// a number every time the form opened).

export const NC_PREFIX = "NC_";

export const formatNcReport = (n) =>
  `${NC_PREFIX}${Number(n).toString(16).toUpperCase()}`;

// NC_1A3 -> 419. Returns NaN for anything that isn't a well-formed number.
export const parseNcReport = (report) => {
  const match = /^NC_([0-9A-F]+)$/i.exec((report || "").trim());
  return match ? parseInt(match[1], 16) : NaN;
};

// The load-bearing side effect carried over from the inventory app,
// tightened per shop practice: ANY live report — open or pending — holds
// the asset out-of-service the moment it's filed. Only closing releases
// it (to junked when the disposition is Junked, otherwise back to active).
export const assetStatusForReport = ({ status, disposition }) => {
  if (status === "closed" && disposition === "junked") return "junked";
  if (status === "closed") return "active";
  return "out-of-service";
};

const REPORT_COUNTER_REF = () =>
  firestore.collection("counters").doc("ncReportNumber");

let unsubscribe = null;

export const fetchNcReportsAsync = createAsyncThunk(
  "ncReports/fetch",
  async (_, thunkAPI) => {
    if (unsubscribe) return;
    thunkAPI.dispatch(setLoading(true));
    unsubscribe = firestore.collection("ncReports").onSnapshot(
      (snapshot) => {
        const items = {};
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (!data.deleted) items[doc.id] = data;
        });
        thunkAPI.dispatch(setNcReports(items));
      },
      (error) => {
        console.error("ncReports listener error", error);
        thunkAPI.dispatch(setError(error.message));
      }
    );
  }
);

export const cancelFetchNcReports = createAsyncThunk(
  "ncReports/cancel",
  async () => {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
  }
);

export const fetchNextNcReportNumberAsync = createAsyncThunk(
  "ncReports/fetchNextReportNumber",
  async (_, thunkAPI) => {
    try {
      const doc = await REPORT_COUNTER_REF().get();
      const last = doc.exists ? Number(doc.data().last) : NaN;
      return Number.isNaN(last) ? null : formatNcReport(last + 1);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const checkNcReportExistsAsync = createAsyncThunk(
  "ncReports/checkReportExists",
  async (report, thunkAPI) => {
    try {
      const snapshot = await firestore
        .collection("ncReports")
        .where("report", "==", report)
        .limit(1)
        .get();
      return !snapshot.empty;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Attachments keep the inventory app's Storage prefix so old and new
// reports share one home; files are never deleted by the app.
export const uploadNcAttachmentAsync = createAsyncThunk(
  "ncReports/uploadAttachment",
  async (file, thunkAPI) => {
    try {
      const filePath = `nonconformance/images/${uuidV4()}/${file.name}`;
      const snapshot = await storage.ref(filePath).put(file);
      const url = await snapshot.ref.getDownloadURL();
      return { url, name: file.name, filePath };
    } catch (error) {
      console.error("uploadNcAttachment failed", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Flip the asset's status in the FIRESTORE assets collection (the RTDB
// registry is being retired). update() — never set() — so a report can
// not create a stub asset record; if the asset isn't migrated yet the
// save still succeeds and the caller is told the status didn't apply.
const applyAssetStatus = async (assetNumber, status) => {
  if (!assetNumber || !status) return { applied: false, reason: "no-op" };
  const ref = firestore.collection("assets").doc(assetNumber);
  try {
    const doc = await ref.get();
    if (!doc.exists) return { applied: false, reason: "missing" };
    await ref.update({
      status,
      statusUpdatedAt: FieldValue.serverTimestamp(),
    });
    return { applied: true };
  } catch (error) {
    console.warn("asset status update failed", error);
    return { applied: false, reason: error.message };
  }
};

// Ask the deployed ncrPDFcreationFs function for a render. Writing a fresh
// `pdfUpdated` value IS the trigger contract; the function renders the
// report and writes `pdfUrl` back onto this doc.
export const requestNcPdfAsync = createAsyncThunk(
  "ncReports/requestPdf",
  async (id, thunkAPI) => {
    try {
      await firestore
        .collection("ncReports")
        .doc(id)
        .update({ pdfUpdated: Date.now() });
      return true;
    } catch (error) {
      console.error("requestNcPdf failed", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const saveNcReportAsync = createAsyncThunk(
  "ncReports/save",
  async ({ id, values, user, autoNumber }, thunkAPI) => {
    try {
      const ref = id
        ? firestore.collection("ncReports").doc(id)
        : firestore.collection("ncReports").doc();
      // pdfUrl/pdfUpdated belong to the PDF pipeline (generate action +
      // function write-back). Omitting them from the merge means a stale
      // dialog snapshot can never overwrite a render that finished while
      // the dialog was open.
      const { pdfUrl, pdfUpdated, ...editable } = values;
      const entry = {
        ...editable,
        deleted: false,
        loggedAt: values.loggedAt || FieldValue.serverTimestamp(),
        loggedBy: values.loggedBy || {
          uid: user?.uid || "",
          name: user?.name || "",
        },
        updatedAt: FieldValue.serverTimestamp(),
      };
      await ref.set(entry, { merge: true });

      // Counter advances only for auto-numbered saves, never backwards.
      const reportNum = parseNcReport(values.report);
      if (autoNumber && !Number.isNaN(reportNum)) {
        try {
          await firestore.runTransaction(async (tx) => {
            const counterRef = REPORT_COUNTER_REF();
            const doc = await tx.get(counterRef);
            const current = doc.exists ? Number(doc.data().last) : NaN;
            if (Number.isNaN(current) || reportNum > current) {
              tx.set(counterRef, { last: reportNum }, { merge: true });
            }
          });
        } catch (error) {
          console.warn("Error updating NC report counter:", error);
        }
      }

      const status = assetStatusForReport(values);
      const assetStatus = await applyAssetStatus(values.assetNumber, status);
      return { id: ref.id, assetStatus, appliedStatus: status };
    } catch (error) {
      console.error("saveNcReport failed", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Soft delete (QMS records keep an audit trail; Storage files stay). If no
// other live report still holds the asset, its status returns to active.
export const deleteNcReportAsync = createAsyncThunk(
  "ncReports/delete",
  async ({ id, assetNumber }, thunkAPI) => {
    try {
      await firestore
        .collection("ncReports")
        .doc(id)
        .set(
          { deleted: true, updatedAt: FieldValue.serverTimestamp() },
          { merge: true }
        );

      let assetStatus = { applied: false, reason: "no-op" };
      if (assetNumber) {
        const snapshot = await firestore
          .collection("ncReports")
          .where("assetNumber", "==", assetNumber)
          .get();
        const stillHeld = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (doc.id !== id && !data.deleted && data.status !== "closed") {
            stillHeld.push(doc.id);
          }
        });
        if (stillHeld.length === 0) {
          assetStatus = await applyAssetStatus(assetNumber, "active");
        }
      }
      return { id, assetStatus };
    } catch (error) {
      console.error("deleteNcReport failed", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const ncReportsSlice = createSlice({
  name: "ncReports",
  initialState: {
    items: {},
    error: null,
    saving: false,
    loading: false,
  },
  reducers: {
    setNcReports(state, action) {
      state.items = action.payload;
      state.loading = false;
      state.error = null;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveNcReportAsync.pending, (state) => {
        state.saving = true;
      })
      .addCase(saveNcReportAsync.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(saveNcReportAsync.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      });
  },
});

export const { setNcReports, setLoading, setError } = ncReportsSlice.actions;

export default ncReportsSlice.reducer;
